import React, { Component, FormEvent } from 'react';
import { MenuPermission, Role } from '../../../domain/rbac/RbacDomains';
import RoleService from '../../../service/RoleService';
import PermissionContainer from './PermissionContainer';
import { isStringValid } from 'util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import LanguageUtil from '../../../util/LanguageUtil';
import { initialRole } from './RoleUtil';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import FormicaInput from '../../custom/FormicaInput';
import FormicaLabel from '../../custom/FormicaLabel';
import RoleHelper from '../../../RoleHelper';
import EnvironmentUtil from '../../../util/EnvironmentUtil';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import AnimatedButton from 'components/custom/AnimatedButton';
import { CheckerRequest } from '../../../domain/maker-checker/MakerCheckerDomains';
import ComponentName from '../../../constant/ComponentName';

class RoleEditComponent extends Component<Props, State> {
  public state = new State();

  private readonly roleService: RoleService = ServiceContainer.getInstance().get(ServiceNames.ROLE);
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  public render() {
    const { role, saveLoading, roleName, roleDescription } = this.state;
    const { isCreate, preview, checkerRequests } = this.props;

    return (
      <div className={'flex flex-col w-full gap-x-2 gap-y-5 items-center'}>
        <div
          style={{
            boxShadow: '2px 2px 15px 0px #0000000D',
          }}
          className={'flex flex-col w-full items-center gap-y-5 pb-5 px-4 py-4 border-[1px] border-[#E0E4E8] rounded-[5px]'}
        >
          <div className={'flex flex-col gap-y-1.5 w-full'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.roleName', defaultMessage: 'Role Name' })} />
            <FormicaInput
              value={roleName}
              onChange={(e) => this.onChangeRoleName(e)}
              name={'name'}
              disabled={!isCreate || !RoleHelper.canManage('Role') || preview}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.roleName', defaultMessage: 'Role Name' })}
            />
          </div>
          <div className={'flex flex-col gap-y-1.5 w-full'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.roleDescription', defaultMessage: 'Role Description' })} />
            <FormicaInput
              value={roleDescription}
              onChange={(e) => this.onChangeDescription(e)}
              name={'description'}
              disabled={!RoleHelper.canManage('Role') || preview}
              placeholder={LanguageUtil.get({ id: 'common.input.placeholder.roleDescription', defaultMessage: 'Role Description' })}
            />
          </div>
        </div>
        <div className={'flex flex-col w-full gap-y-10'}>
          <div
            style={{ boxShadow: '2px 2px 15px 0px #0000000D' }}
            className={'flex flex-col gap-y-2 w-full border-[1px] border-[#E0E4E8] rounded-[5px] px-4 py-4 max-h-[calc(100dvh-500px)] overflow-y-scroll'}
          >
            {role && <PermissionContainer key={role.name} menu={role.menu} onMenuChange={this.props.onRoleMenuChange} />}
          </div>
        </div>
        <div className={'self-end 2xl:pb-0 pb-4'}>
          <AnimatedButton
            onClick={this.save}
            loading={saveLoading}
            disabled={
              !RoleHelper.canManage('Role') ||
              role.name == 'Admin' ||
              role.name == 'Member' ||
              (isCreate && this.environmentUtil.isLdapOnly()) ||
              !!checkerRequests.find((c) => c.component == ComponentName.ADMIN_ROLE && c.documentId == roleName) ||
              preview
            }
            customSize={'sm'}
            icon={'save'}
            customType={'outlined'}
            text={LanguageUtil.get({ id: 'common.button.save', defaultMessage: 'Save' })}
          />
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const { isCreate, selectedRole } = this.props;
    if (!isCreate && selectedRole) {
      this.setState({ role: selectedRole, roleName: selectedRole.name, roleDescription: selectedRole.description });
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    const { selectedRole } = this.props;
    if (prevProps.selectedRole != selectedRole) {
      if (selectedRole) {
        this.setState({ role: selectedRole, roleName: selectedRole.name, roleDescription: selectedRole.description });
      }
    }
  }

  private onChangeRoleName = (e: FormEvent<HTMLInputElement>) => {
    const { role } = this.state;
    const name: string = e.currentTarget.value.trim();
    if (!RoleHelper.canManage('Role')) return;
    if (name == 'create' || !isStringValid(name) || name == 'Admin' || name == 'admin') {
      return MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.role', defaultMessage: 'Role' }),
        detail: (
          <div>
            {role.name}{' '}
            {LanguageUtil.get({
              id: 'messages.warning.inappropriate',
              defaultMessage: 'You used inappropriate characters',
            })}
          </div>
        ),
      });
    }
    this.setState({ roleName: name });
  };

  private onChangeDescription = (e: React.FormEvent<HTMLInputElement>) => {
    this.setState({ roleDescription: e.currentTarget.value });
  };

  private save = async () => {
    const { role, roleName, roleDescription } = this.state;
    const { isCreate, isCreateChange, onHide, checkerRequests, preview } = this.props;
    if (preview) return;
    if (role.name == 'Admin' || role.name == 'Member') return;
    if (!!checkerRequests.find((c) => c.component == ComponentName.ADMIN_ROLE && c.documentId == roleName)) {
      MessageUtil.warning({
        summary: LanguageUtil.get({ id: 'common.label.waitingApproval' }),
        detail: LanguageUtil.get({
          id: 'messages.warning.waitingApproval',
          defaultMessage: "Create or Update request has been already sent to other admin users, you can't send new request",
        }),
        life: 4000,
      });
      return;
    }

    if (!RoleHelper.canManage('Role') || (isCreate && this.environmentUtil.isLdapOnly())) return;
    if (role == undefined || roleName == undefined) return;
    if (roleName) {
      role.name = roleName;
      role.description = roleDescription || '';
    }
    this.setState({ saveLoading: true });
    try {
      let response;
      if (isCreate) {
        role.id = role.name;
        this.setState({ role });
        response = await this.roleService.saveRole(role.name, role, isCreate);
        isCreateChange?.();
      } else {
        this.setState({ role });
        response = await this.roleService.saveRole(role.name, role, isCreate);
      }
      if (response) {
        onHide?.();
      }
    } catch (e) {
    } finally {
      this.setState({ saveLoading: false });
    }
  };
}

interface Props {
  selectedRole?: Role;
  isCreate: boolean;
  onRoleChange: (role: Role) => void;
  onRoleMenuChange: (menu: MenuPermission[]) => void;
  isCreateChange: () => void;
  onHide: () => void;
  checkerRequests: CheckerRequest[];
  preview?: boolean;
}

class State {
  role: Role = initialRole();
  saveLoading: boolean = false;
  roleName: string | undefined;
  roleDescription: string | undefined;
}

export default RoleEditComponent;
